import React, { Suspense, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Bicycle, Bus, Car, House, PersonSimpleWalk, X } from '@phosphor-icons/react';
import type { HomeCommuteState } from '../../../../../../hooks/useHomeCommute';
import type { CommuteMode } from '../../../../../../redux/features/jobFormSlice';
import { commuteModeOrder } from '../../../../../../utils/map/commute-mode-order';
import { commuteModeLabel, formatCommuteMinutes } from './commute-copy';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { setHomeAddress } from '../../../../../../redux/features/jobFormSlice';
import { searchAddressSuggestions, type AddressSuggestion } from '../../../../../../utils/map/geocode-address';
import { trackEvent } from '../../../../../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../../../../../i18n-review/client/Msg';

const CompanyLocationMap = React.lazy(() =>
  import('../../../../../../components/CompanyLocationMap').then((m) => ({ default: m.CompanyLocationMap })),
);

export interface JobLocationMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName?: string;
  companyId?: string;
  locations: Array<{ lat: number; lng: number; address?: string; isPrimary: boolean }>;
  /** Travel times from the user's saved home address (useHomeCommute, fetched while this modal is open). */
  commute?: HomeCommuteState | null;
  brandColor?: string;
}

const MODE_ICONS: Record<CommuteMode, React.ComponentType<{ className?: string; weight?: 'duotone' | 'regular' | 'bold' }>> = {
  transit: Bus,
  driving: Car,
  cycling: Bicycle,
  walking: PersonSimpleWalk,
};

/**
 * Address search inside the map modal, so a first-time user never has to leave the job to get a
 * travel time. Same Mapbox geocoding and the same profile field as the profile page's own
 * home-address input (the pick is dispatched to `jobForm.homeAddress`, persisted locally for
 * anonymous visitors and synced with the profile for signed-in ones); nothing typed here goes to
 * our backend, only the chosen label + coordinates are saved.
 */
function HomeAddressInlineField({ accent, onSaved }: { accent: string; onSaved: () => void }): React.ReactElement {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const formatMessage = useFormatMessage();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  useEffect(() => {
    if (query.trim().length < 3) {
      setSuggestions([]);
      return undefined;
    }
    const token = import.meta.env.PUBLIC_ENV__MAPBOX_ACCESS_TOKEN as string | undefined;
    if (!token) return undefined;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      searchAddressSuggestions({ query, accessToken: token, language: intl.locale, signal: controller.signal })
        .then(setSuggestions)
        .catch(() => {
          if (!controller.signal.aborted) setSuggestions([]);
        });
    }, 300);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query, intl.locale]);

  return (
    <div className="relative mt-2">
      <input
        type="text"
        autoComplete="off"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={formatMessage({ id: 'profile.homeAddress.placeholder', defaultMessage: 'Search an address…' })}
        aria-label={formatMessage({ id: 'jobDetailPageV2.homeCommute.addressLabel', defaultMessage: 'Your home address', description: 'Accessible label of the home-address search field inside the job location map' })}
        className="w-full min-w-0 rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-[13px] text-[#1E1A14] placeholder:text-[#7A6E5F]/70 focus:outline-none focus:ring-2 dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF] dark:placeholder:text-[#8A7060]/80"
        style={{ ['--tw-ring-color' as string]: `${accent}55` }}
      />
      {suggestions.length > 0 ? (
        <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-[#DDD5C8] bg-white shadow-lg dark:border-[#4A3520] dark:bg-[#241A0F]">
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <button
                type="button"
                onClick={() => {
                  dispatch(setHomeAddress({ label: suggestion.label, point: suggestion.point }));
                  trackEvent({ name: 'profile_media_action', params: { action: 'home_address_set', locale: intl.locale, surface: 'job_map' } });
                  setQuery('');
                  setSuggestions([]);
                  onSaved();
                }}
                className="block w-full px-3 py-2 text-left text-[13px] text-[#1E1A14] hover:bg-[#F8F5F0] dark:text-[#F2EADF] dark:hover:bg-[#2A1D0F]/50"
              >
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="m-0 mt-1.5 text-[11px] text-[#8C8474]">
        <Msg id="jobDetailPageV2.homeCommute.privacyNote" defaultMessage="Saved to your profile and only used for travel times. Never shared with employers." description="Privacy note under the home-address field inside the job location map" />
      </p>
    </div>
  );
}

/**
 * The travel strip above the map: one tile per mode that has a figure, the user's preferred mode
 * (public transport unless they picked another on their profile) first and filled in the brand
 * colour. Only ever rendered client-side after the user opened the map, so it costs the page
 * nothing; users without a saved home address get a one-line prompt instead of tiles.
 */
function HomeCommuteStrip({ commute, brandColor }: { commute: HomeCommuteState; brandColor?: string }): React.ReactElement | null {
  const formatMessage = useFormatMessage();
  const accent = brandColor || '#3B5E45';
  const [editing, setEditing] = useState(false);
  const shell = 'border-b border-[#DDD5C8] dark:border-[#4A3520] px-4 py-3 shrink-0 text-[12.5px]';

  if (!commute.hasHome || editing) {
    return (
      <div className={shell}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[#6E6659] dark:text-[#B0A896]">
          <House className="h-4 w-4 flex-none" weight="duotone" aria-hidden style={{ color: accent }} />
          <span>
            {commute.hasHome ? (
              <Msg id="jobDetailPageV2.homeCommute.changeHome" defaultMessage="Search a new home address." description="Prompt above the address field in the job location map when the user is changing an already saved home address" />
            ) : (
              <Msg id="jobDetailPageV2.homeCommute.addHome" defaultMessage="Add your home address once and you'll see your commute time on every job." description="Prompt in the job location map when the user has no saved home address" />
            )}
          </span>
          {commute.hasHome ? (
            <button type="button" onClick={() => setEditing(false)} className="text-[11.5px] font-bold underline-offset-2 hover:underline" style={{ color: accent }}>
              <Msg id="jobDetailPageV2.homeCommute.cancelChange" defaultMessage="Keep current" description="Button that abandons changing the saved home address in the job location map" />
            </button>
          ) : null}
        </div>
        <HomeAddressInlineField accent={accent} onSaved={() => setEditing(false)} />
      </div>
    );
  }

  const modes = commuteModeOrder(commute.preferredMode);
  const available = modes.filter((m) => typeof commute.durations?.[m] === 'number');
  const loading = commute.status === 'loading' || commute.status === 'idle';

  return (
    <div className={shell}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[#6E6659] dark:text-[#B0A896]">
        <House className="h-4 w-4 flex-none" weight="duotone" aria-hidden style={{ color: accent }} />
        <span className="font-semibold text-[#16150F] dark:text-[#F3EFE4]">
          {formatMessage({ id: 'jobDetailPageV2.homeCommute.fromHome', defaultMessage: 'From {home}', description: 'Heading of the travel-time strip in the job location map; {home} is the saved home address label' }, { home: commute.homeLabel })}
        </span>
        <button type="button" onClick={() => setEditing(true)} className="text-[11.5px] font-bold underline-offset-2 hover:underline" style={{ color: accent }}>
          <Msg id="jobDetailPageV2.homeCommute.change" defaultMessage="Change" description="Button in the job location map travel strip that lets the user replace the saved home address" />
        </button>
        {loading ? (
          <span className="text-[11.5px]">
            <Msg id="jobDetailPageV2.homeCommute.calculating" defaultMessage="Calculating travel times…" description="Shown in the job location map while travel times from the saved home address are being fetched" />
          </span>
        ) : null}
        {!loading && available.length === 0 ? (
          <span className="text-[11.5px]">
            <Msg id="jobDetailPageV2.homeCommute.unavailable" defaultMessage="Travel times aren't available for this address." description="Shown in the job location map when no route could be computed from the saved home address" />
          </span>
        ) : null}
      </div>
      {available.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {available.map((mode, index) => {
            const Icon = MODE_ICONS[mode];
            const minutes = commute.durations?.[mode] as number;
            const primary = index === 0;
            return (
              <div
                key={mode}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 ${primary ? '' : 'border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#16150F] dark:text-[#F3EFE4]'}`}
                style={primary ? { background: accent, borderColor: accent, color: '#FFFFFF' } : undefined}
              >
                <Icon className="h-4 w-4 flex-none" weight="duotone" aria-hidden />
                <span className="[font-family:'Space_Grotesk',sans-serif] text-[15px] font-bold leading-none">{formatCommuteMinutes(formatMessage, minutes)}</span>
                <span className={`text-[11px] font-semibold ${primary ? 'opacity-90' : 'text-[#6E6659] dark:text-[#B0A896]'}`}>{commuteModeLabel(formatMessage, mode)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
      {!loading && available.length > 0 && !available.includes('transit') ? (
        <p className="m-0 mt-2 text-[11px] text-[#8C8474]">
          <Msg id="jobDetailPageV2.homeCommute.noTransit" defaultMessage="Public transport times aren't available yet." description="Footnote in the job location map travel strip when only road-based travel times could be computed" />
        </p>
      ) : null}
    </div>
  );
}

/**
 * TEC-512 editorial-rail redesign, design decision #10: the headline facts' "Map" chip opens
 * this modal instead of the old always-inline collapsible location card. Deliberately reuses
 * `CompanyLocationMap` as-is (same two-tier marker language, same frosted popup, same top-right
 * NavigationControl; nothing about the map itself is new) inside the same `fixed inset-0`
 * dialog shell every other modal on this page already uses (see TailorCvModal.tsx), rather than
 * inventing a bespoke overlay. Known gap carried over on purpose, not fixed here: CompanyLocationMap
 * is light-mode-only with hardcoded hex colors while the SRP's own MapView.tsx already swaps
 * Mapbox styles and uses --jc-* tokens for its themed popup chrome; reconciling that is a
 * separate, larger piece of work than this ticket's job-card scope.
 */
export function JobLocationMapModal({ isOpen, onClose, companyName, companyId, locations, commute = null, brandColor }: JobLocationMapModalProps): React.ReactElement | null {
  const formatMessage = useFormatMessage();
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={formatMessage({
        id: 'jobDetailPageV2.locationModal.title',
        defaultMessage: 'Job location',
        description: 'Location map modal title, editorial-rail redesign',
      })}
    >
      <div
        className="relative flex flex-col w-full max-w-4xl h-[85vh] max-h-[720px] bg-white dark:bg-[#1F1509] rounded-2xl shadow-2xl overflow-hidden border border-[#DDD5C8]/60 dark:border-[#4A3520]/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#DDD5C8] dark:border-[#4A3520] shrink-0">
          <h3 className="m-0 [font-family:'Space_Grotesk',sans-serif] text-[14.5px] font-bold text-[#16150F] dark:text-[#F3EFE4]">
            <Msg
              id="jobDetailPageV2.locationModal.headingWithCount"
              defaultMessage="{count, plural, one {Job location} other {Job locations · {count} sites}}"
              values={{ count: locations.length }}
              description="Location map modal heading, editorial-rail redesign; shows a site count when the employer has more than one location"
            />
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-[#7A6E5F] dark:text-[#B8A48C] cursor-pointer"
            aria-label={formatMessage({ id: 'jobDetailPageV2.locationModal.close', defaultMessage: 'Close map', description: 'Close button for the location map modal' })}
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>
        {commute ? <HomeCommuteStrip commute={commute} brandColor={brandColor} /> : null}
        <div className="flex-1 min-h-0">
          <Suspense fallback={<div className="w-full h-full bg-jc-page/50 animate-pulse" aria-hidden="true" />}>
            <CompanyLocationMap locations={locations} companyName={companyName} companyId={companyId} className="h-full" />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
